import { useEffect, useState } from "react";
import { Modal, Button, Space, Typography } from "antd";
import {
  DownloadOutlined,
  ShareAltOutlined,
  PlusOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

export default function InstallPwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({
    isIOS: false,
    isAndroid: false,
    isMobile: false,
    isStandalone: false
  });

  // Function to clear all app data
  const clearAllAppData = () => {
    try {
      localStorage.clear();

      sessionStorage.clear();

      document.cookie.split(";").forEach((c) => {
        const eqPos = c.indexOf("=");
        const name = eqPos > -1 ? c.substr(0, eqPos) : c;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
      });

      // Clear IndexedDB (if used)
      if ('indexedDB' in window) {
        indexedDB.databases().then(databases => {
          databases.forEach(db => {
            indexedDB.deleteDatabase(db.name);
          });
        }).catch(console.error);
      }

      // Clear Cache Storage (Service Worker caches)
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName);
          });
        }).catch(console.error);
      }

      
    } catch (error) {
      console.error('Error clearing app data:', error);
    }
  };

  // Check if app is running in standalone mode and monitor for changes
  const monitorStandaloneMode = () => {
    const checkStandaloneStatus = () => {
      const isCurrentlyStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;

      const wasStandalone = localStorage.getItem('pwa-was-standalone') === 'true';

      // If it was standalone but now it's not, the app might have been uninstalled
      if (wasStandalone && !isCurrentlyStandalone) {
        
        clearAllAppData();
      }

      // Update the standalone status
      localStorage.setItem('pwa-was-standalone', isCurrentlyStandalone.toString());
    };

    // Check immediately
    checkStandaloneStatus();

    // Monitor display-mode changes
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    mediaQuery.addListener(checkStandaloneStatus);

    return () => {
      mediaQuery.removeListener(checkStandaloneStatus);
    };
  };

  const setupPeriodicCheck = () => {
    const APP_HEALTH_KEY = 'creditX-creditX-health-check';
    const HEALTH_CHECK_INTERVAL = 24 * 60 * 60 * 1000;

    const updateHealthCheck = () => {
      localStorage.setItem(APP_HEALTH_KEY, Date.now().toString());
    };

    const checkAppHealth = () => {
      const lastCheck = localStorage.getItem(APP_HEALTH_KEY);
      const now = Date.now();

      if (lastCheck) {
        const timeSinceLastCheck = now - parseInt(lastCheck);

        if (timeSinceLastCheck > 7 * 24 * 60 * 60 * 1000) {
          
          clearAllAppData();
        }
      }

      updateHealthCheck();
    };

    // Initial check
    checkAppHealth();

    // Set up periodic checks
    const interval = setInterval(checkAppHealth, HEALTH_CHECK_INTERVAL);

    // Update health check when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateHealthCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };

  useEffect(() => {
    const detectDevice = () => {
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );
      const isSmallScreen = window.innerWidth <= 768;
      const isMobile = isMobileDevice || isSmallScreen;

      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
      return {
        isIOS,
        isAndroid,
        isMobile,
        isStandalone
      };
    };

    const info = detectDevice();
    setDeviceInfo(info);

    // Set up monitoring and cleanup systems
    const cleanupStandaloneMonitor = monitorStandaloneMode();
    const cleanupPeriodicCheck = setupPeriodicCheck();

    const lastShown = localStorage.getItem("pwa-install-prompt-shown");
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000;

    // iOS manual install instructions
    if (info.isIOS && info.isMobile && !info.isStandalone) {
      if (!lastShown || now - parseInt(lastShown) > oneWeek) {
        setTimeout(() => {
          setIsVisible(true);
        }, 2000);
      }
    }

    // Android native prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (info.isMobile && !info.isStandalone) {
        if (!lastShown || now - parseInt(lastShown) > oneWeek) {
          setIsVisible(true);
        }
      }
    };

    const handleAppInstalled = () => {
      setIsVisible(false);
      localStorage.setItem("pwa-install-prompt-shown", Date.now().toString());
      localStorage.setItem('pwa-was-standalone', 'true');
      
    };

    // Enhanced app uninstall detection for supported browsers
    const handleBeforeUnload = () => {
      // This won't catch uninstalls, but helps with regular page unloads
      localStorage.setItem('pwa-last-session', Date.now().toString());
    };

    const handleResize = () => {
      const newInfo = detectDevice();
      setDeviceInfo(newInfo);
      if (!newInfo.isMobile) {
        setIsVisible(false);
      }
    };

    // Event listeners
    if (!info.isIOS) {
      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    }
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("resize", handleResize);

    // Cleanup function
    return () => {
      if (!info.isIOS) {
        window.removeEventListener(
          "beforeinstallprompt",
          handleBeforeInstallPrompt
        );
      }
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("resize", handleResize);

      // Cleanup monitoring systems
      cleanupStandaloneMonitor();
      cleanupPeriodicCheck();
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    setLoading(true);
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        
        localStorage.setItem("pwa-install-prompt-shown", Date.now().toString());
        localStorage.setItem('pwa-was-standalone', 'true');
      } else {
        
      }
    } catch (error) {
      console.error("Error during installation:", error);
    } finally {
      setDeferredPrompt(null);
      setLoading(false);
      setIsVisible(false);
    }
  };

  const handleCancel = () => {
    setIsVisible(false);
  };

  const handleRemindLater = () => {
    setIsVisible(false);
    const threeDays = 3 * 24 * 60 * 60 * 1000;
    localStorage.setItem(
      "pwa-install-prompt-shown",
      (Date.now() - 7 * 24 * 60 * 60 * 1000 + threeDays).toString()
    );
  };

  // Debug function to manually clear data (for testing)
  const handleManualClear = () => {
    if (window.confirm('Are you sure you want to clear all app data? This action cannot be undone.')) {
      clearAllAppData();
      alert('All app data has been cleared!');
    }
  };

  if (!deviceInfo.isMobile || deviceInfo.isStandalone || !isVisible) {
    return null;
  }

  // iOS-specific
  if (deviceInfo.isIOS) {
    return (
      <Modal
        title={
          <Space align="center" size="small">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <DownloadOutlined style={{ color: "white", fontSize: "16px" }} />
            </div>
            <Title level={5} style={{ margin: 0, color: "#1890ff" }}>
              Install App
            </Title>
          </Space>
        }
        open={isVisible}
        onCancel={handleCancel}
        width={340}
        centered
        maskClosable
        closable={false}
        styles={{
          body: { padding: "24px 0 16px" },
          header: { paddingBottom: 16, borderBottom: "1px solid #f0f8ff" },
          mask: { padding: "0 20px" }
        }}
        footer={
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              padding: "8px 0"
            }}
          >
            <Button
              size="middle"
              onClick={handleCancel}
              style={{
                borderColor: "#d9d9d9",
                minWidth: "70px"
              }}
            >
              Not Now
            </Button>
            <Button
              type="primary"
              size="middle"
              onClick={handleRemindLater}
              style={{
                background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                border: "none",
                boxShadow: "0 2px 8px rgba(24, 144, 255, 0.3)",
                minWidth: "100px"
              }}
            >
              Remind Me
            </Button>
          </div>
        }
      >
        <div
          style={{
            padding: "0 20px",
            color: "#595959"
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            Install this app on your iPhone for a better experience!
          </div>

          <div style={{ marginBottom: "16px" }}>
            <Text strong style={{ color: "#1890ff", fontSize: "16px" }}>
              How to install:
            </Text>
          </div>

          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              alignItems: "flex-start"
            }}
          >
            <span
              style={{
                marginRight: "12px",
                color: "#1890ff",
                fontWeight: "bold",
                minWidth: "16px"
              }}
            >
              1.
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap"
              }}
            >
              <Text>Tap the share button </Text>
              <ShareAltOutlined
                style={{
                  margin: "0 4px",
                  color: "#1890ff",
                  fontSize: "16px"
                }}
              />
              <Text> in Safari</Text>
            </div>
          </div>

          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              alignItems: "flex-start"
            }}
          >
            <span
              style={{
                marginRight: "12px",
                color: "#1890ff",
                fontWeight: "bold",
                minWidth: "16px"
              }}
            >
              2.
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap"
              }}
            >
              <Text>Select "Add to Home Screen" </Text>
              <PlusOutlined
                style={{
                  margin: "0 4px",
                  color: "#1890ff",
                  fontSize: "14px",
                  border: "1px solid #1890ff",
                  borderRadius: "2px",
                  padding: "2px"
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <span
              style={{
                marginRight: "12px",
                color: "#1890ff",
                fontWeight: "bold",
                minWidth: "16px"
              }}
            >
              3.
            </span>
            <Text>Tap "Add" to install the app</Text>
          </div>

          {/* Debug button - remove in production */}
          {/* {process.env.NODE_ENV === 'development' && (
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <Button size="small" onClick={handleManualClear} danger>
                Clear All Data (Debug)
              </Button>
            </div>
          )} */}
        </div>
      </Modal>
    );
  }

  // Android / other browsers
  return (
    <Modal
      title={
        <Space align="center" size="small">
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <DownloadOutlined style={{ color: "white", fontSize: "16px" }} />
          </div>
          <Title level={5} style={{ margin: 0, color: "#1890ff" }}>
            Install App
          </Title>
        </Space>
      }
      open={isVisible}
      onCancel={handleCancel}
      width={320}
      centered
      maskClosable
      closable={false}
      styles={{
        body: { padding: "24px 0 16px" },
        header: { paddingBottom: 16, borderBottom: "1px solid #f0f8ff" },
        mask: { padding: "0 20px" }
      }}
      footer={
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            padding: "8px 0"
          }}
        >
          <Button
            size="middle"
            onClick={handleCancel}
            style={{
              borderColor: "#d9d9d9",
              minWidth: "70px"
            }}
          >
            Not Now
          </Button>
          <Button
            type="primary"
            size="middle"
            icon={<DownloadOutlined />}
            loading={loading}
            onClick={handleInstallClick}
            style={{
              background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
              border: "none",
              boxShadow: "0 2px 8px rgba(24, 144, 255, 0.3)",
              minWidth: "80px"
            }}
          >
            Install
          </Button>
        </div>
      }
    >
      <div
        style={{
          textAlign: "center",
          padding: "0 20px",
          color: "#595959"
        }}
      >
        Would you like to install this app for quick access?

        {/* Debug button - remove in production */}
        {/* {process.env.NODE_ENV === 'development' && (
          <div style={{ marginTop: "16px" }}>
            <Button size="small" onClick={handleManualClear} danger>
              Clear All Data (Debug)
            </Button>
          </div>
        )} */}
      </div>
    </Modal>
  );
}